import { useEffect, useMemo, useState } from "react";

import { Alert, AlertIcon, AlertTitle } from "@/components/elements/Alert";
import { PrimaryButton, SecondaryButton } from "@/components/elements/Button";
import { CronDisplayReadable } from "@/components/elements/CronDisplayReadable";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from "@/components/elements/Modal/Modal";
import {
  CronInput,
  TimeInput,
  useSyncScheduleContext,
} from "@/components/elements/SyncScheduler";
import {
  SyncScheduleRadioGroup,
  SyncSchedulerMenu,
  WeldSyncScheduler,
} from "@/components/modules/WeldSyncScheduler";
import FieldLabel from "@/components/primitives/InputLabel";
import {
  fullSyncScheduleOptions,
  fullSyncScheduleOutpacesSync,
} from "@/features/elt/fullSyncSchedule";

const DAILY_CRON = "0 0 * * *";
const WEEKLY_CRON = "0 0 * * 0";

function defaultFullSyncCron(options: { value: string }[]): string {
  const preferred = [DAILY_CRON, WEEKLY_CRON].find((cron) =>
    options.some((option) => option.value === cron),
  );
  return preferred ?? options[0]?.value ?? DAILY_CRON;
}

/**
 * The scheduler keeps the expression the field holds, valid or not, but only hands valid ones to
 * `onChange` - so the footer has to be told what is really on screen.
 */
function PickedCronReporter(props: {
  onChange: (picked: { cron: string; valid: boolean }) => void;
}) {
  const { value, error } = useSyncScheduleContext();
  const { onChange } = props;
  const cron = value?.value;
  const valid = !error && cron != null;
  useEffect(() => {
    if (cron != null) {
      onChange({ cron, valid });
    }
  }, [cron, valid, onChange]);
  return null;
}

export const FullSyncScheduleModal = (props: {
  streamName: string;
  /** The cron the table syncs on, which a full sync schedule cannot outpace. */
  syncInterval?: string | null;
  currentSchedule: string | null;
  /** Explains why the picker opened by itself when capturing deletes asked for it. */
  requiredForCaptureDeletes?: boolean;
  onSave: (cron: string) => void;
  onRemove: () => void;
  onCancel: () => void;
}) => {
  const options = useMemo(
    () => fullSyncScheduleOptions(props.syncInterval),
    [props.syncInterval],
  );
  const initialCron = props.currentSchedule ?? defaultFullSyncCron(options);
  const [localCron, setLocalCron] = useState<string>(initialCron);
  const [picked, setPicked] = useState({ cron: initialCron, valid: true });
  const outpacesSync =
    picked.valid &&
    fullSyncScheduleOutpacesSync(picked.cron, props.syncInterval);

  return (
    <Modal isOpen onClose={props.onCancel} size="lg">
      <ModalCloseButton />
      <ModalHeader className="flex items-center">
        <div className="text-lg">
          Schedule full syncs for{" "}
          <code className="rounded-sm bg-gray-200 px-1 dark:bg-gray-800">
            {props.streamName}
          </code>
        </div>
      </ModalHeader>
      <ModalBody className="py-4">
        <div className="mt-4">
          <div className="mb-4 space-y-2 text-sm">
            <p>
              {props.requiredForCaptureDeletes
                ? "This table syncs incrementally, which doesn't capture deletes. A full sync re-syncs all data, including deletes."
                : "This table syncs incrementally. A full sync re-syncs all data from the source."}
            </p>
            <p>
              Full syncs don't run on their own: when the schedule comes due,
              the next regular sync runs as a full sync.
            </p>
            {props.syncInterval && (
              <p className="text-muted-foreground">
                Regular sync schedule:{" "}
                <CronDisplayReadable cron={props.syncInterval} />.
              </p>
            )}
          </div>
          {outpacesSync && (
            <Alert status="warning" className="mb-4 items-start">
              <AlertIcon className="mt-0.5" />
              <div>
                <AlertTitle className="mb-1">
                  Every sync will be a full sync
                </AlertTitle>
                <p className="text-sm">
                  This schedule is more frequent than the table&apos;s regular
                  sync. Choose a longer interval to keep incremental syncs in
                  between.
                </p>
              </div>
            </Alert>
          )}
          <div className="mt-4">
            <WeldSyncScheduler
              cron={localCron}
              options={options.length > 0 ? options : undefined}
              onChange={(cron) => {
                if (cron) {
                  setLocalCron(cron);
                }
              }}
            >
              {({ value, mode, showTimeOffsetInput }) => {
                return (
                  <>
                    <PickedCronReporter onChange={setPicked} />
                    <div className="flex flex-col gap-4">
                      <div>
                        <p className="mb-2 text-sm text-muted-foreground">
                          How often should this table run a full sync?
                        </p>
                        <CronInput />
                        <SyncScheduleRadioGroup className="grid grid-cols-2 gap-x-4 gap-y-3" />
                        {showTimeOffsetInput && (
                          <>
                            <p className="mt-4 mb-2 text-sm text-muted-foreground">
                              Starting at
                            </p>
                            <TimeInput />
                          </>
                        )}
                      </div>
                      <div>
                        <FieldLabel>Schedule description:</FieldLabel>
                        <div className="flex items-center gap-4 text-sm">
                          <span>
                            {value && (
                              <CronDisplayReadable verbose cron={value.value} />
                            )}
                            {!value && mode === "custom" && (
                              <span>
                                Write a cron expression to see update frequency
                                here
                              </span>
                            )}
                          </span>
                          <SyncSchedulerMenu />
                        </div>
                      </div>
                    </div>
                  </>
                );
              }}
            </WeldSyncScheduler>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="flex justify-between">
        <SecondaryButton
          disabled={props.currentSchedule === null}
          onClick={props.onRemove}
        >
          Remove schedule
        </SecondaryButton>
        <div className="flex gap-4">
          <SecondaryButton onClick={props.onCancel}>Cancel</SecondaryButton>
          <PrimaryButton
            isDisabled={!picked.valid}
            onClick={() => {
              if (picked.valid) {
                props.onSave(picked.cron);
              }
            }}
          >
            Save schedule
          </PrimaryButton>
        </div>
      </ModalFooter>
    </Modal>
  );
};
